// ── BCHNostr — load a pubkey's business profile (kind 30499) ──────
//
// Author-filtered by design: an addressable event's `d` tag is not a
// trust boundary, so we only ever read a business profile scoped to a
// specific author. Returns the parsed profile (or null) plus loading.

import { useMemo } from 'react';
import { useNostrQuery } from './useNostrQuery';
import {
  BUSINESS_KIND, BUSINESS_D_TAG, parseBusiness, type BusinessProfile,
} from '../lib/business';

export interface UseBusinessProfileResult {
  business: BusinessProfile | null;
  loading: boolean;
  refetch: () => void;
}

export function useBusinessProfile(pubkey: string | undefined): UseBusinessProfileResult {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey
      ? [{ kinds: [BUSINESS_KIND], authors: [pubkey], '#d': [BUSINESS_D_TAG], limit: 1 }]
      : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const business = useMemo(() => {
    if (events.length === 0) return null;
    // Newest wins (addressable events are replaceable).
    const newest = [...events].sort((a, b) => b.created_at - a.created_at)[0];
    return parseBusiness(newest);
  }, [events]);

  return { business, loading, refetch };
}
